/*
====================================================================
AdminSubmissions.jsx

Three separate actions per row, each with its own dialog — no more
"open the viewer, then find an Edit button inside it":

  eye     -> SubmissionPreview   read-only, looks exactly like the PDF
  pencil  -> SubmissionEditForm  live editable form, prefilled
  pdf     -> direct download
  bin     -> delete

Filters:
  - search box (matches patient name, email, or form name)
  - form type filter dropdown (auto-populated from loaded data)
  - date filter (matches submission date)
  - client ID filter
  - pagination (rows per page + page controls)

Table now shows Client ID instead of Form Type (Form Type is still
filterable via the dropdown above the table).

The old SubmissionView.jsx (and SubmissionEditform.js) are replaced by
those two components and can be deleted.
====================================================================
*/

import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Tooltip,
    Chip,
    Snackbar,
    Alert,
    TextField,
    InputAdornment,
    MenuItem,
    Stack
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import BadgeIcon from "@mui/icons-material/Badge";
import EventIcon from "@mui/icons-material/Event";

import {
    getSubmissions,
    deleteSubmission,
    downloadSubmissionPDF
} from "./formManagementApi";

import SubmissionPreview from "./SubmissionPreview";
import SubmissionEditForm from "./SubmissionEditForm";


const AdminSubmissions = () => {

    const [submissions, setSubmissions] = useState([]);

    // two independent dialogs — viewing never turns into editing
    const [viewId, setViewId] = useState(null);
    const [editId, setEditId] = useState(null);

    const [toast, setToast] = useState(null);

    // ---- search / filter state ----
    const [searchTerm, setSearchTerm] = useState("");
    const [formTypeFilter, setFormTypeFilter] = useState("all");
    const [dateFilter, setDateFilter] = useState("");       // yyyy-mm-dd
    const [clientIdFilter, setClientIdFilter] = useState("");

    // ---- pagination state ----
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const role = localStorage.getItem("role");
    const canDelete = role === "admin" || role === "doctor";


    const loadSubmissions = useCallback(async () => {
        try {
            const data = await getSubmissions();
            setSubmissions(data);
        } catch (error) {
            console.log("Submission Fetch Error", error);
            setToast({ severity: "error", message: "Could not load submissions." });
        }
    }, []);

    useEffect(() => {
        loadSubmissions();
    }, [loadSubmissions]);

    const removeSubmission = async (id) => {
        if (!window.confirm("Are you sure you want to delete?")) return;

        try {
            await deleteSubmission(id);
            loadSubmissions();
            setToast({ severity: "success", message: "Submission deleted." });
        } catch (error) {
            console.log("Submission Delete Error", error);
            setToast({ severity: "error", message: "Could not delete submission." });
        }
    };

    const downloadPdf = async (id) => {
        try {
            await downloadSubmissionPDF(id);
        } catch (error) {
            console.log("PDF Download Error", error);
            setToast({ severity: "error", message: "Could not generate the PDF." });
        }
    };

    // distinct form types found in the current data, for the filter dropdown
    const formTypes = useMemo(() => {
        const types = new Set(
            submissions
                .map((item) => item.form_type)
                .filter((type) => Boolean(type))
        );
        return Array.from(types).sort();
    }, [submissions]);



    // apply search + form type + date + client id filters
    const filteredSubmissions = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        const clientTerm = clientIdFilter.trim().toLowerCase();

        return submissions.filter((item) => {
            const matchesType =
                formTypeFilter === "all" || item.form_type === formTypeFilter;

            if (!matchesType) return false;

            if (dateFilter) {
                const itemDate = item.created_at
                    ? new Date(item.created_at).toISOString().slice(0, 10)
                    : null;
                if (itemDate !== dateFilter) return false;
            }

            if (clientTerm) {
                const clientId = String(item.client_id ?? "").toLowerCase();
                if (!clientId.includes(clientTerm)) return false;
            }

            if (!term) return true;

            const haystack = [
                item.client_name,
                item.email,
                item.form_name
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return haystack.includes(term);
        });
    }, [submissions, searchTerm, formTypeFilter, dateFilter, clientIdFilter]);

    // reset to first page whenever the filters change so results are never
    // "stuck" on an empty page
    useEffect(() => {
        setPage(0);
    }, [searchTerm, formTypeFilter, dateFilter, clientIdFilter]);

    const paginatedSubmissions = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredSubmissions.slice(start, start + rowsPerPage);
    }, [filteredSubmissions, page, rowsPerPage]);

    const handleChangePage = (_event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box sx={{ padding: "25px" }}>

            <Typography variant="h5" fontWeight="600" mb={3}>
                Patient Submitted Forms
            </Typography>

            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                mb={2}
                alignItems={{ xs: "stretch", sm: "center" }}
                flexWrap="wrap"
                useFlexGap
            >
                <TextField
                    size="small"
                    placeholder="Search by patient name, email, or form name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ minWidth: { sm: 320 } }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        )
                    }}
                />

                <TextField
                    select
                    size="small"
                    label="Form Type"
                    value={formTypeFilter}
                    onChange={(e) => setFormTypeFilter(e.target.value)}
                    sx={{ minWidth: 180 }}
                >
                    <MenuItem value="all">All Form Types</MenuItem>
                    {formTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                            {type}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    size="small"
                    type="date"
                    label="Date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 170 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <EventIcon fontSize="small" />
                            </InputAdornment>
                        )
                    }}
                />

                <TextField
                    size="small"
                    placeholder="Client ID"
                    value={clientIdFilter}
                    onChange={(e) => setClientIdFilter(e.target.value)}
                    sx={{ minWidth: 160 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <BadgeIcon fontSize="small" />
                            </InputAdornment>
                        )
                    }}
                />
            </Stack>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>S.No</TableCell>
                            <TableCell>Patient Name</TableCell>
                            <TableCell>Form Name</TableCell>
                            <TableCell>Client ID</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {paginatedSubmissions.map((item, index) => (
                            <TableRow key={item.id} hover>

                                <TableCell>{page * rowsPerPage + index + 1}</TableCell>

                                <TableCell>
                                    <Typography>
                                        {item.client_name || "N/A"}
                                    </Typography>
                                    <Typography fontSize="12px" color="gray">
                                        {item.email}
                                    </Typography>
                                </TableCell>

                                <TableCell>{item.form_name}</TableCell>

                                <TableCell>
                                    <Chip label={item.client_id ?? "N/A"} size="small" />
                                </TableCell>

                                <TableCell>
                                    {new Date(item.created_at).toLocaleString()}
                                </TableCell>

                                <TableCell align="right">

                                    <Tooltip title="View form">
                                        <IconButton
                                            color="primary"
                                            onClick={() => setViewId(item.id)}
                                        >
                                            <VisibilityIcon />
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Edit / complete form">
                                        <IconButton
                                            color="info"
                                            onClick={() => setEditId(item.id)}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Download PDF">
                                        <IconButton
                                            color="success"
                                            onClick={() => downloadPdf(item.id)}
                                        >
                                            <PictureAsPdfIcon />
                                        </IconButton>
                                    </Tooltip>

                                   { !canDelete && (
                                        <Tooltip title="Delete">
                                            <IconButton
                                                color="error"
                                                onClick={() => removeSubmission(item.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                </TableCell>

                            </TableRow>
                        ))}

                        {filteredSubmissions.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                                    <Typography color="text.secondary">
                                        {submissions.length === 0
                                            ? "No submitted forms yet."
                                            : "No submissions match your search."}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>

                <TablePagination
                    component="div"
                    count={filteredSubmissions.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
            </TableContainer>

            {/* VIEW — read-only, identical to the PDF */}
            {viewId && (
                <SubmissionPreview
                    id={viewId}
                    open
                    close={() => setViewId(null)}
                />
            )}

            {/* EDIT — live form, prefilled with what the patient submitted */}
            {editId && (
                <SubmissionEditForm
                    id={editId}
                    open
                    close={() => setEditId(null)}
                    onSaved={() => {
                        loadSubmissions();
                        setToast({ severity: "success", message: "Submission updated." });
                    }}
                />
            )}

            <Snackbar
                open={Boolean(toast)}
                autoHideDuration={4000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                {toast && (
                    <Alert severity={toast.severity} onClose={() => setToast(null)}>
                        {toast.message}
                    </Alert>
                )}
            </Snackbar>

        </Box>
    );
};

export default AdminSubmissions;